import React from 'react'
import { Save, Loader2, ChevronUp, ChevronDown, CheckCircle, Settings, Code, Check, X, Undo, Redo, Search, FileText, ZoomIn, ZoomOut, WrapText, Map, MoreHorizontal, Calendar, BarChart3, FileCode } from 'lucide-react'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import ExecuteButton, { type ExecuteButtonHandle } from '@/components/jobs/ExecuteButton'
import DatasetSelector from './DatasetSelector'
import type { Dataset } from '@/types/dataset'
import MetadataConfigModal from '@/components/scripts/MetadataConfigModal'
import PineScriptExportModal from '@/components/scripts/PineScriptExportModal'
import type { DatasetMetadata } from '@/types/metadata'
import { useTranslation } from 'react-i18next'
import { BacktestButton } from '@/components/backtest'

interface TitleBarProps {
  scriptName: string
  onNameChange: (name: string) => void
  onSaveName?: (name: string) => Promise<void>
  isCompiled: boolean
  hasUnsavedChanges: boolean
  isSaving: boolean
  isSavingAndRunning: boolean
  isLoading: boolean
  isNew: boolean
  scriptId: string
  selectedDatasetId: string
  onDatasetChange: (datasetId: string) => void
  datasets: Dataset[]
  selectedSymbol?: string
  onSymbolChange?: (symbol: string) => void
  onJobCreated?: (jobId: string) => void
  currentMetadata: DatasetMetadata | null
  onMetadataUpdated: () => void
  onSave: () => void
  onSaveBeforeRun: () => Promise<string>
  dslCode: string
  headerCollapsed: boolean
  onToggleHeader: () => void
  editor?: any // Monaco editor instance
  sidebarCollapsed?: boolean
  onToggleSidebar?: () => void
  onBacktestConfigClick?: () => void
  onBacktestRun?: (jobId: string) => void
  onSaveBeforeBacktest?: () => Promise<string>
  /** Quick date filter (toolbar, left of Run) */
  dateStart?: string
  dateEnd?: string
  datePreset?: string
  onDateStartChange?: (value: string) => void
  onDateEndChange?: (value: string) => void
  onDatePresetChange?: (presetId: string) => void
  datePresets?: readonly { id: string; name: string }[]
}

export default function TitleBar({
  scriptName,
  onNameChange,
  onSaveName,
  isCompiled,
  hasUnsavedChanges,
  isSaving,
  isSavingAndRunning,
  isLoading,
  isNew,
  scriptId,
  selectedDatasetId,
  onDatasetChange,
  datasets,
  selectedSymbol,
  onSymbolChange,
  onJobCreated,
  currentMetadata,
  onMetadataUpdated,
  onSave,
  onSaveBeforeRun,
  dslCode,
  headerCollapsed,
  onToggleHeader,
  editor,
  sidebarCollapsed = false,
  onToggleSidebar,
  onBacktestConfigClick,
  onBacktestRun,
  onSaveBeforeBacktest,
  dateStart = '',
  dateEnd = '',
  datePreset = '',
  onDateStartChange: _onDateStartChange,
  onDateEndChange: _onDateEndChange,
  onDatePresetChange,
  datePresets = [],
}: TitleBarProps) {
  const { t } = useTranslation()
  const executeButtonRef = React.useRef<ExecuteButtonHandle>(null)

  // Local state for name editing
  const [isEditingName, setIsEditingName] = React.useState(false)
  const [tempName, setTempName] = React.useState(scriptName)
  const [isSavingName, setIsSavingName] = React.useState(false)
  
  // Local state for session config modal
  const [sessionConfigOpen, setSessionConfigOpen] = React.useState(false)
  // Local state for Pine Script export modal
  const [pineScriptExportOpen, setPineScriptExportOpen] = React.useState(false)

  // Update temp name when scriptName changes externally
  React.useEffect(() => {
    setTempName(scriptName)
  }, [scriptName])

  const handleNameEditStart = () => {
    setTempName(scriptName)
    setIsEditingName(true)
  }

  const handleNameConfirm = async () => {
    if (tempName.trim() !== scriptName && tempName.trim()) {
      setIsSavingName(true)
      try {
        if (onSaveName) {
          // Use specific save name function if provided
          await onSaveName(tempName.trim())
        } else {
          // Fallback to onNameChange (updates form state only)
          onNameChange(tempName.trim())
        }
      } finally {
        setIsSavingName(false)
      }
    }
    setIsEditingName(false)
  }

  const handleNameCancel = () => {
    setTempName(scriptName)
    setIsEditingName(false)
  }

  // Monaco Editor Actions
  const executeEditorAction = (actionId: string) => {
    if (editor) {
      try {
        editor.getAction(actionId)?.run()
      } catch (error) {
        console.warn(`Editor action ${actionId} not available:`, error)
      }
    }
  }

  const toggleWordWrap = () => {
    if (editor) {
      const currentWordWrap = editor.getRawOptions().wordWrap || 'off'
      editor.updateOptions({
        wordWrap: currentWordWrap === 'on' ? 'off' : 'on'
      })
    }
  }

  const toggleMinimap = () => {
    if (editor) {
      const currentMinimap = editor.getRawOptions().minimap
      editor.updateOptions({
        minimap: { enabled: !currentMinimap?.enabled }
      })
    }
  }

  const zoomIn = () => {
    if (editor) {
      const currentFontSize = editor.getRawOptions().fontSize || 14
      editor.updateOptions({ fontSize: Math.min(currentFontSize + 1, 24) })
    }
  }

  const zoomOut = () => {
    if (editor) {
      const currentFontSize = editor.getRawOptions().fontSize || 14
      editor.updateOptions({ fontSize: Math.max(currentFontSize - 1, 8) })
    }
  }

  if (headerCollapsed) {
    return (
      <div className="h-9 flex items-center justify-between px-4 border-b bg-background/98 backdrop-blur-sm flex-shrink-0 text-xs relative shadow-sm">
        <div className="flex items-center gap-3 min-w-0">
          <Code className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <span className="text-sm font-medium text-muted-foreground truncate">
            {scriptName || t('scripts.untitled')}
          </span>
          {hasUnsavedChanges && (
            <div className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0" title="Unsaved changes" />
          )}
          {isCompiled && (
            <div title="Compiled successfully">
              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
            </div>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleHeader}
          className="h-7 w-7 rounded-md opacity-70 hover:opacity-100 hover:bg-muted transition-all"
          title="Show header"
        >
          <ChevronDown className="w-4 h-4" />
        </Button>
      </div>
    )
  }

  return (
    <div className="h-12 flex items-center justify-between px-4 border-b bg-background/98 backdrop-blur-sm flex-shrink-0 text-xs relative shadow-sm">
      {/* Left Section - Script Info */}
      <div className="flex items-center gap-4 min-w-0 flex-1">
        {/* Editor Actions Menu */}
        {editor && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 rounded-md opacity-70 hover:opacity-100 hover:bg-muted transition-all mr-2"
                title="Editor options"
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              <DropdownMenuItem onClick={() => executeEditorAction('undo')} className="flex items-center gap-2">
                <Undo className="w-4 h-4" />
                <span>Undo</span>
                <span className="ml-auto text-xs text-muted-foreground">Ctrl+Z</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => executeEditorAction('redo')} className="flex items-center gap-2">
                <Redo className="w-4 h-4" />
                <span>Redo</span>
                <span className="ml-auto text-xs text-muted-foreground">Ctrl+Y</span>
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              <DropdownMenuItem onClick={() => executeEditorAction('actions.find')} className="flex items-center gap-2">
                <Search className="w-4 h-4" />
                <span>Find</span>
                <span className="ml-auto text-xs text-muted-foreground">Ctrl+F</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => executeEditorAction('editor.action.startFindReplaceAction')} className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                <span>Replace</span>
                <span className="ml-auto text-xs text-muted-foreground">Ctrl+H</span>
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              <DropdownMenuItem onClick={() => executeEditorAction('editor.action.formatDocument')} className="flex items-center gap-2">
                <Code className="w-4 h-4" />
                <span>Format Document</span>
                <span className="ml-auto text-xs text-muted-foreground">Alt+Shift+F</span>
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              <DropdownMenuItem onClick={zoomIn} className="flex items-center gap-2">
                <ZoomIn className="w-4 h-4" />
                <span>Zoom In</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={zoomOut} className="flex items-center gap-2">
                <ZoomOut className="w-4 h-4" />
                <span>Zoom Out</span>
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              <DropdownMenuItem onClick={toggleWordWrap} className="flex items-center gap-2">
                <WrapText className="w-4 h-4" />
                <span>Toggle Word Wrap</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={toggleMinimap} className="flex items-center gap-2">
                <Map className="w-4 h-4" />
                <span>Toggle Minimap</span>
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              <DropdownMenuItem onClick={onToggleSidebar} className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                <span>{sidebarCollapsed ? 'Show Tools' : 'Hide Tools'}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <div className="flex items-center gap-2 min-w-0">
          <Code className="w-4 h-4 text-primary flex-shrink-0" />
          {isEditingName ? (
            <>
              <Input
                value={tempName}
                onChange={(e) => setTempName(e.target.value)}
                placeholder={isNew ? t('scripts.untitled') : t('scripts.scriptName')}
                className="h-7 px-2 text-sm font-medium border border-primary/50 bg-background focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0 flex-shrink-0 min-w-0 max-w-48"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleNameConfirm()
                  } else if (e.key === 'Escape') {
                    handleNameCancel()
                  }
                }}
                autoFocus
              />
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleNameConfirm}
                  disabled={isSavingName || !tempName.trim()}
                  className="h-6 w-6 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-950"
                  title="Save name"
                >
                  {isSavingName ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Check className="w-3 h-3" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleNameCancel}
                  disabled={isSavingName}
                  className="h-6 w-6 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                  title="Cancel editing"
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            </>
          ) : (
            <div
              className="h-7 px-2 text-sm font-medium flex items-center cursor-pointer hover:bg-muted/50 rounded min-w-0 max-w-48 truncate"
              onClick={handleNameEditStart}
              title="Click to edit script name"
            >
              {scriptName || t('scripts.untitled')}
            </div>
          )}
        </div>

        {/* Status Indicators */}
        <div className="flex items-center gap-2">
          {hasUnsavedChanges && (
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-orange-500/10 border border-orange-500/20">
              <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
              <span className="text-xs font-medium text-orange-600 dark:text-orange-400">Unsaved</span>
            </div>
          )}
          {isCompiled && (
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-green-500/10 border border-green-500/20">
              <CheckCircle className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
              <span className="text-xs font-medium text-green-600 dark:text-green-400">Compiled</span>
            </div>
          )}
        </div>
      </div>

      {/* Center Section - Dataset */}
      <div className="flex items-center gap-3">
        <DatasetSelector
          selectedDatasetId={selectedDatasetId}
          onDatasetChange={onDatasetChange}
          datasets={datasets}
          selectedSymbol={selectedSymbol}
          onSymbolChange={onSymbolChange}
        />

        {!isNew && (
          <>
            <MetadataConfigModal
              scriptId={scriptId}
              currentMetadata={currentMetadata}
              onMetadataUpdated={onMetadataUpdated}
              open={sessionConfigOpen}
              onOpenChange={setSessionConfigOpen}
            />
            {onBacktestConfigClick && (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-3 text-xs font-medium"
                      title="Configure"
                    >
                      <Settings className="w-3.5 h-3.5 mr-2" />
                      Configure
                      <ChevronDown className="w-3.5 h-3.5 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setSessionConfigOpen(true)}>
                      <Calendar className="w-4 h-4 mr-2" />
                      <span>Configure Session</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => {
                      if (onBacktestConfigClick) {
                        onBacktestConfigClick()
                      }
                    }}>
                      <BarChart3 className="w-4 h-4 mr-2" />
                      <span>Configure Backtesting</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setPineScriptExportOpen(true)}>
                      <FileCode className="w-4 h-4 mr-2" />
                      <span>Export to Pine Script</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <PineScriptExportModal
                  scriptId={scriptId}
                  scriptName={scriptName}
                  open={pineScriptExportOpen}
                  onOpenChange={setPineScriptExportOpen}
                />
                <BacktestButton
                  scriptId={scriptId}
                  selectedDatasetId={selectedDatasetId}
                  variant="outline"
                  size="sm"
                  className="h-8 px-3 text-xs font-medium"
                  onJobCreated={onBacktestRun}
                  onSaveBeforeRun={onSaveBeforeBacktest}
                />
              </>
            )}
          </>
        )}
      </div>

      {/* Right Section - Date filter + Actions */}
      <div className="flex items-center gap-2">
        {datePresets.length > 0 && onDatePresetChange && (
          <div className="flex items-center gap-1.5 rounded-md border bg-muted/40 px-2 py-1">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <Select
              value={datePreset || 'custom'}
              onValueChange={(value) => {
                if (value === 'custom') {
                  onDatePresetChange?.('')
                  executeButtonRef.current?.openModal()
                  return
                }
                onDatePresetChange?.(value)
              }}
            >
              <SelectTrigger className="h-7 min-w-[90px] border-0 bg-transparent shadow-none focus:ring-0 text-xs font-medium">
                <SelectValue placeholder={t('results.dateRange', 'Date range')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="custom">{t('results.custom', 'Custom')}</SelectItem>
                {datePresets.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="h-6 w-px bg-border" />

        <Button
          onClick={onSave}
          disabled={isSaving || isLoading || isSavingAndRunning}
          variant={hasUnsavedChanges ? 'default' : 'outline'}
          size="sm"
          className="h-8 px-3 text-xs font-medium transition-all"
        >
          {isSaving ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" />
          ) : (
            <Save className="w-3.5 h-3.5 mr-2" />
          )}
          {isNew ? t('common.create') : t('common.save')}
        </Button>

        <ExecuteButton
          ref={executeButtonRef}
          scriptId={scriptId}
          dslCode={dslCode}
          selectedDatasetId={selectedDatasetId}
          onDatasetChange={onDatasetChange}
          selectedSymbol={selectedSymbol}
          initialDateStart={dateStart}
          initialDateEnd={dateEnd}
          initialDatePreset={datePreset}
          variant="default"
          disabled={isSaving || isLoading || isSavingAndRunning || !dslCode.trim() || !selectedDatasetId}
          hasUnsavedChanges={hasUnsavedChanges}
          isNew={isNew}
          onSaveBeforeRun={onSaveBeforeRun}
          onJobCreated={onJobCreated}
        />

        <div className="h-6 w-px bg-border mx-2" />

        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleHeader}
          className="h-7 w-7 rounded-md opacity-70 hover:opacity-100 hover:bg-muted transition-all"
          title="Hide header"
        >
          <ChevronUp className="w-4 h-4" />
        </Button>
      </div>
    </div>
  )
}
